import { action, extend, types } from "@gadgetinc/mobx-quick-tree";
import { intersection, sortBy, uniq } from "lodash";
import { contentHash } from "../../utils";
import type { AnyShopifyAppConfig } from "../ShopifyConnectionConfig";
import { ShopifyBaseConfig } from "./ShopifyBaseConfig";
import { ProtectedAccessModels } from "./ShopifyModelMetadata";
import { getShopifyModelApiIdentifierByModelKey } from "./apiIdentifiers";
import type { ShopifyConnectionMetadataObject } from "./file-based-actions/shopifyConnectionMetadataSchema";
import { ShopifyCustomerKey, ShopifyGdprRequestKey, ShopifyShopKey } from "./keys";
import { allScopeIds, isAuthenticatedScope, isUnauthenticatedScope, normalizeScopes } from "./utils";

/**
 * See packages/state-trees/src/connections/ShopifyConnectionConfig.ts for documentation
 * on the different types of Shopify configurations
 */
export const ShopifyBasePartnerConfig = <Name extends string, T extends AnyShopifyAppConfig>(name: Name, AppConfig: T) => {
  class BasicPartnerConfig extends extend(ShopifyBaseConfig(name, AppConfig), {
    requestedScopes: types.array(types.string),
    offlineModeRoles: types.array(types.string),
    customerAuthenticationEnabled: types.maybe(types.boolean),
  }) {
    get contentHash() {
      const scopes = uniq(this.requestedScopes).slice().sort();
      const modelKeys = uniq(this.enabledModelKeys).slice().sort();
      return contentHash([
        this.type,
        this.shopifyApiVersion,
        this.customerAuthenticationEnabled,
        ...scopes,
        ...modelKeys,
        ...sortBy(this.appConfigsAsArray, ["createdDate", "key"]).map((config) => config.contentHash),
        ...this.offlineModeRoles.slice().sort(),
      ]);
    }
    get requestedScopesAsArray() {
      return Array.from(this.requestedScopes.values());
    }
    get authenticatedRequestedScopesAsArray() {
      return Array.from(this.requestedScopes.values()).filter((scope) => isAuthenticatedScope(scope));
    }
    get unauthenticatedRequestedScopesAsArray() {
      return Array.from(this.requestedScopes.values()).filter((scope) => isUnauthenticatedScope(scope));
    }
    get unknownRequestedScopesAsArray() {
      return Array.from(this.requestedScopes.values()).filter((scope) => !allScopeIds.includes(scope));
    }
    get hasProtectedAccessScopes() {
      return this.shopifyApiVersion >= "2022-10" && intersection(this.enabledModelKeysAsArray, ProtectedAccessModels).length > 0;
    }
    get normalizedScopes() {
      return normalizeScopes(this.requestedScopesAsArray);
    }
    get metadataObject(): ShopifyConnectionMetadataObject {
      return {
        apiVersion: this.baseMetadataObject.apiVersion,
        enabledModels: this.baseMetadataObject.enabledModels.filter(
          (model) =>
            model !== getShopifyModelApiIdentifierByModelKey(ShopifyShopKey) &&
            model !== getShopifyModelApiIdentifierByModelKey(ShopifyGdprRequestKey)
        ),
        type: "partner",
        scopes: this.requestedScopesAsArray,
        customerAuthenticationEnabled: this.customerAuthenticationEnabled,
      };
    }
    @action
    setOfflineModeRoles(roles: string[]) {
      this.offlineModeRoles.replace(roles);
    }
    @action
    setRequestedScopes(requestedScopes: string[]) {
      this.requestedScopes.replace(requestedScopes);
    }
    @action
    addRequestedScopes(scopes: string[]) {
      this.requestedScopes.push(...scopes);
      this.requestedScopes.replace(this.normalizedScopes);
    }
    @action
    setCustomerAuthenticationEnabled(value: boolean | undefined) {
      this.customerAuthenticationEnabled = value;
      if (value) {
        this.setEnabledModelKey(ShopifyCustomerKey);
      }
    }
  }
  return BasicPartnerConfig;
};
